// AuthForm.jsx — the sign-in form. One implementation, one palette.
//
// This is the form only; it never draws its own window. LoginGate is the single surface that
// presents it (full-screen for signed-out visitors, dismissible when something inside the app
// asks for an account). AuthPage used to render it at /auth as well; that route was deleted on
// 2026-09-13 because nothing ever linked to it — the deep-link contract it documented had no
// producer, and the mobile redirect it claimed to catch actually returns to the page it left
// from, where AuthContext's handleRedirectResult picks it up.
//
// TWO PALETTES, ONE IMPLEMENTATION. `light` gives the white card used for the in-app prompt
// (a shared video link, the generate wall); the default dark one is the full-screen gate.
//
// This used to say "deliberately NO light variant", because a white sign-in card had existed as
// a SEPARATE component and kept rendering on top of the dark gate. That was a stacking bug, and
// what actually fixed it was making LoginGateProvider the only thing that mounts a sign-in
// surface, with route beating intent — not banning the colour. Two skins over one form cannot
// stack; two components could. The rule worth keeping is ONE FORM, which this still is.
//
// House style, do not "improve" these away: no spinners, no gradients, no backdrop blur.
import React, { useId, useState } from "react";
import { Mail, ArrowLeft } from "lucide-react";
import {
  signInWithGoogle,
  signUpWithEmail,
  signInWithEmail,
  sendPasswordReset,
} from "../auth";
import { useT } from "../i18n/useT";
import { browserCountry, minimumSignupAge } from "../utils/constants";

const PRIVACY_URL = "https://www.iubenda.com/privacy-policy/52362461";
const TERMS_URL = "https://www.iubenda.com/terms-and-conditions/52362461";

// Resolved ONCE at module load, not per render: the visitor's timezone cannot change while the
// sign-in form is open, and this sits in a component that re-renders on every keystroke in the
// email field. See browserCountry() for why the answer is allowed to be wrong.
const MIN_AGE = minimumSignupAge(browserCountry());

const GoogleLogo = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);


export const AuthForm = ({
  title,
  subtitle,
  onSuccess,
  compact = false,
  hideHeading = false,
  // White card for the in-app prompt; dark for the full-screen gate. See the note at the top.
  light = false,
  // Keep the email form behind a button until asked for. Google carries almost all real
  // sign-ins, so showing two empty input boxes by default makes the panel look like work.
  collapseEmail = false,
  // Renders the EMAIL PATH ONLY — no Google button, no OR divider. This is the second step
  // of the identity-first flow both ChatGPT and Claude use: the first screen offers Google
  // AND email together, and the screen you land on after choosing email does not offer
  // Google again. Repeating it there is the bug this exists to fix — it reads as "that
  // didn't work, try Google instead" at the moment the person has already chosen.
  emailOnly = false,
}) => {
  const t = useT();
  // Field ids must be unique per INSTANCE — AuthForm can be mounted more than once at a time
  // (the always-mounted pages plus a routed one), and duplicate ids would point every label
  // at the first copy's input.
  const fieldId = useId();
  // "signin" | "signup" | "reset"
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(null); // "google" | "email" | null
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [emailOpen, setEmailOpen] = useState(emailOnly || !collapseEmail);

  // ONE palette. There is no light variant: every sign-in surface in the app is the dark
  // card, so a white sheet can never appear over the gate again.
  // Every colour in the markup below goes through this object, so a variant is a token swap
  // and never a second copy of the form.
  const ui = light ? {
    heading: "text-gray-900",
    body: "text-gray-500",
    // Google is bordered rather than filled here: on white, a filled button would out-shout
    // the page behind it, and the prompt is meant to sit ON the content, not replace it.
    google:
      "w-full py-3 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 hover:border-gray-300 transition-colors flex items-center justify-center gap-3 disabled:opacity-60",
    secondary:
      "w-full py-3 px-4 bg-[#635BFF] text-white rounded-xl font-semibold hover:bg-[#4B44CC] transition-colors flex items-center justify-center gap-2 disabled:opacity-60",
    // border, not border-2. A 2px outline on a pale grey reads as a DRAWN BOX rather than a
    // field — it was the loudest thing in the card. Both references use a hairline.
    input:
      "w-full px-3.5 py-2.5 text-[15px] bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-[#635BFF] focus:ring-1 focus:ring-[#635BFF] focus:outline-none transition-colors",
    label: "text-gray-700",
    rule: "bg-gray-200",
    // gray-500, NOT gray-400. This token carries the Terms/Privacy/age disclosure at 11px, and
    // gray-400 on white measures 2.54:1 — under the 4.5:1 AA floor for normal text and under
    // even the 3:1 large-text floor. gray-500 is 4.83:1 and is the same grey the landing page
    // already uses for body copy, so this is the house tone rather than an overcorrection.
    muted: "text-gray-500",
    link: "text-gray-500 hover:text-gray-700",
    notice: "bg-green-50 text-green-700",
    error: "bg-red-50 text-red-600",
  } : {
    heading: "text-white",
    body: "text-gray-400",
    // Primary action is WHITE on dark: it is the highest-contrast element on the panel,
    // which is what makes Google the obvious default rather than one option among several.
    google:
      "w-full py-3.5 px-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-3 disabled:opacity-60",
    secondary:
      "w-full py-3.5 px-4 bg-[#1E1E21] border border-[#2C2C31] text-white rounded-xl font-semibold hover:bg-[#26262A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60",
    input:
      "w-full px-3.5 py-2.5 text-[15px] bg-[#1E1E21] border border-[#2C2C31] rounded-lg text-white focus:border-[#635BFF] focus:ring-1 focus:ring-[#635BFF] focus:outline-none transition-colors",
    label: "text-gray-300",
    rule: "bg-[#2C2C31]",
    // Mirror of the light case: gray-500 on the #131313 card is 3.84:1, also under AA. gray-400
    // reads as the LIGHTER swatch but on a dark ground it is the higher-contrast one (7.32:1).
    muted: "text-gray-400",
    link: "text-gray-400 hover:text-gray-200",
    notice: "bg-[#123322] text-green-300",
    error: "bg-[#3A1A1A] text-red-300",
  };

  const finish = (result) => {
    if (!result?.success) {
      setError(result?.message || t("generic_error"));
      return;
    }
    // An unconfirmed account is handed straight to VerifyEmailWall, which App.jsx renders
    // INSTEAD of the app. Closing here is what lets it through — holding the modal open would
    // stack two identical messages on top of each other.
    if (onSuccess) onSuccess();
  };

  const handleGoogle = async () => {
    setError("");
    setBusy("google");
    try {
      const result = await signInWithGoogle();
      // Mobile redirects away mid-call; the landing page finishes the job.
      if (result?.success) finish(result);
      else if (result) setError(result.message || t("generic_error"));
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
        setError(t("generic_error"));
      }
    } finally {
      setBusy(null);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (mode === "reset") {
      setBusy("email");
      const result = await sendPasswordReset(email.trim());
      setBusy(null);
      if (result.success) {
        setNotice(t("auth_reset_sent"));
        setMode("signin");
      } else {
        setError(result.message);
      }
      return;
    }

    setBusy("email");
    const result =
      mode === "signup"
        ? await signUpWithEmail(email.trim(), password)
        : await signInWithEmail(email.trim(), password);
    setBusy(null);
    finish(result);
  };

  // NO "verify" MODE HERE ANY MORE. This used to render its own "check your email" panel after
  // signup, which became a duplicate the moment VerifyEmailWall existed: the modal said it,
  // and the wall behind the modal said the same thing better — it polls, it can resend, and it
  // offers a way out. One message, one place. The form's job ends when the credential lands.

  const isReset = mode === "reset";
  const isSignup = mode === "signup";

  return (
    <div>
      <div className={`${hideHeading ? "hidden" : ""} ${compact ? "text-center mb-5" : "text-center mb-6"}`}>
        {isReset ? (
          <>
            <h2 className={`text-xl font-bold mb-1 ${ui.heading}`}>{t("auth_reset_title")}</h2>
            <p className={`text-sm ${ui.body}`}>{t("auth_reset_desc")}</p>
          </>
        ) : (
          <>
            {/* A caller-supplied title describes the mode the form was OPENED in. Once the
                visitor switches to Create account it is no longer true — the card said
                "Sign in to Magi / Pick up where you left off" above a signup form with a
                name field and a Create account button. The override therefore applies to
                sign-in only, and signup always uses its own words. */}
            <h2 className={`text-xl font-bold mb-1 ${ui.heading}`}>
              {isSignup ? t("auth_signup_title") : (title || t("auth_signin_title"))}
            </h2>
            <p className={`text-sm ${ui.body}`}>
              {isSignup ? t("auth_signup_desc") : (subtitle || t("auth_signin_desc"))}
            </p>
          </>
        )}
      </div>

      {notice && (
        <div className={`mb-4 px-3 py-2 rounded-lg text-sm text-center ${ui.notice}`}>
          {notice}
        </div>
      )}
      {error && (
        <div className={`mb-4 px-3 py-2 rounded-lg text-sm text-center ${ui.error}`} role="alert">
          {error}
        </div>
      )}

      {!isReset && !emailOnly && (
        <>
          <button
            onClick={handleGoogle}
            disabled={busy !== null}
            className={ui.google}
          >
            <GoogleLogo />
            {busy === "google" ? t("auth_signing_in") : t("auth_continue_google")}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className={`h-px flex-1 ${ui.rule}`} />
            <span className={`text-xs uppercase tracking-wide ${ui.muted}`}>{t("auth_or")}</span>
            <div className={`h-px flex-1 ${ui.rule}`} />
          </div>

          {!emailOpen && (
            <button onClick={() => setEmailOpen(true)} className={ui.secondary}>
              <Mail className="w-4 h-4" />
              {t("auth_continue_email")}
            </button>
          )}
        </>
      )}

      <form onSubmit={handleEmailSubmit} className={`space-y-3.5 ${emailOpen || isReset ? "" : "hidden"}`}>
        {/* LABELS, not placeholders. A placeholder disappears the moment you type, so the
            field stops saying what it is exactly when you might want to check — and a
            screen reader gets no accessible name at all. The label text reuses the existing
            *_placeholder strings, so this adds no new keys to translate. */}
        {/* NO NAME FIELD. It was optional (no `required`) but looked mandatory sitting above
            two required inputs, so it bought friction at the highest-drop-off point in the
            funnel for something every consumer already falls back on: the sidebar and profile
            menu use the email prefix, Brevo personalisation degrades to blank, and Settings
            can set a real name later, in a context where volunteering one means something.
            Google signups still supply a name for free via the provider profile. */}
        <div>
          <label htmlFor={`${fieldId}-email`} className={`block text-[13px] font-medium mb-1.5 ${ui.label}`}>
            {t("auth_email_placeholder")}
          </label>
          <input
            id={`${fieldId}-email`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className={ui.input}
          />
        </div>
        {!isReset && (
          <div>
            <label htmlFor={`${fieldId}-password`} className={`block text-[13px] font-medium mb-1.5 ${ui.label}`}>
              {t("auth_password_placeholder")}
            </label>
            <input
              id={`${fieldId}-password`}
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignup ? "new-password" : "current-password"}
              className={ui.input}
            />
          </div>
        )}

        {/* NO ICON. An envelope on a sign-in button reads as "send us mail"; the button
            submits a password, it does not send anything. Neither ChatGPT nor Claude puts a
            glyph on this action. The Mail icon still belongs on the COLLAPSED "Continue with
            email" button above, where it labels a choice between methods. */}
        <button
          type="submit"
          disabled={busy !== null}
          className={`${ui.secondary} mt-1`}
        >
          {busy === "email"
            ? t("auth_working")
            : isReset
              ? t("auth_reset_submit")
              : isSignup
                ? t("auth_signup_submit")
                : t("auth_signin_submit")}
        </button>
      </form>

      <div className={`mt-5 text-center text-sm ${emailOpen || isReset ? "" : "hidden"}`}>
        {isReset ? (
          <button
            onClick={() => { setMode("signin"); setError(""); }}
            className="text-[#635BFF] font-medium inline-flex items-center gap-1 hover:underline"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {t("auth_back_to_signin")}
          </button>
        ) : (
          <>
            <button
              onClick={() => { setMode(isSignup ? "signin" : "signup"); setError(""); setNotice(""); }}
              className={ui.link}
            >
              {isSignup ? t("auth_have_account") : t("auth_no_account")}{" "}
              <span className="text-[#635BFF] font-medium">
                {isSignup ? t("auth_signin_link") : t("auth_signup_link")}
              </span>
            </button>
            {!isSignup && (
              <div className="mt-2">
                <button
                  onClick={() => { setMode("reset"); setError(""); }}
                  className={`text-sm ${ui.link}`}
                >
                  {t("auth_forgot_password")}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <p className={`text-[11px] leading-snug text-center mt-5 ${ui.muted}`}>
        {t("auth_agree_prefix")}
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-[#635BFF]">
          {t("auth_terms")}
        </a>
        {t("auth_and")}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="text-[#635BFF]">
          {t("auth_privacy")}
        </a>.{" "}
        {/* The Terms say "Users must be older than 13" and, until now, nothing on the way in
            said so — the claim existed only behind a link nobody opens. Stating it at the point
            of sign-in is what makes it an attestation rather than fine print, and it is the
            whole of what replaced the deleted age gate (33cb8fe9). Deliberately a STATEMENT and
            not a checkbox or a birthday field: a self-declared date buys no more legal cover
            than this line, and collecting a child's birthday to decide whether we may hold a
            child's data is the trade the age gate lost on. */}
        {t("auth_min_age", { age: MIN_AGE })}
      </p>
    </div>
  );
};

export default AuthForm;
